import React, { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building, Plus, LogIn, Pencil, ImagePlus, Users, Wifi, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", segment: "", description: "", admin_name: "", admin_email: "", admin_password: "", plan_name: "", max_users: "", max_connections: "" };
const num = (v) => (v === "" || v === null || v === undefined ? 0 : parseInt(v, 10) || 0);

export const TenantLogo = ({ tenant, className = "w-10 h-10" }) => (
  tenant?.logo_url
    ? <img src={`${BACKEND_URL}${tenant.logo_url}`} alt={tenant.name} className={`${className} rounded-xl object-contain bg-white border border-border`} />
    : <div className={`${className} rounded-xl bg-emerald-600 text-white font-bold flex items-center justify-center text-sm`}>
        {(tenant?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
      </div>
);

const LogoButton = ({ t, onDone }) => {
  const ref = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/platform/tenants/${t.id}/logo`, fd); toast.success("Logo atualizada"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha ao enviar logo"); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => ref.current?.click()} data-testid={`tenant-logo-${t.id}`}><ImagePlus className="w-4 h-4" /></Button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </>
  );
};

function TenantDialog({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const editing = !!initial?.id;
  useEffect(() => { if (open) setF({ ...EMPTY, ...(initial || {}), max_users: initial?.max_users ?? "", max_connections: initial?.max_connections ?? "", plan_name: initial?.plan_name || "" }); }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const plan = { plan_name: f.plan_name || "", max_users: num(f.max_users), max_connections: num(f.max_connections) };
    try {
      if (editing) await api.patch(`/platform/tenants/${initial.id}`, { name: f.name, segment: f.segment || null, description: f.description || null, ...plan });
      else await api.post("/platform/tenants", { name: f.name, segment: f.segment || null, description: f.description || null,
        admin_name: f.admin_name, admin_email: f.admin_email, admin_password: f.admin_password, ...plan });
      toast.success(editing ? "Empresa atualizada" : "Empresa criada");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Preencha todos os campos corretamente (senha com 6+ caracteres)" : d || "Falha ao salvar");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="tenant-dialog">
        <DialogHeader><DialogTitle>{editing ? "Editar empresa" : "Nova empresa"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome da empresa</Label><Input value={f.name} onChange={set("name")} data-testid="tenant-name-input" /></div>
          <div><Label>Segmento / atividade</Label><Input value={f.segment || ""} onChange={set("segment")} placeholder="Ex.: Loja de roupas, Clínica, Imobiliária" data-testid="tenant-segment-input" /></div>
          <div><Label>Descrição (usada pela IA)</Label><Textarea value={f.description || ""} onChange={set("description")} rows={2} data-testid="tenant-description-input" /></div>
          <div className="border-t border-border pt-3">
            <div className="text-sm font-semibold mb-2">Plano</div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Nome do plano</Label><Input value={f.plan_name} onChange={set("plan_name")} placeholder="Ex.: Básico" data-testid="tenant-plan-input" /></div>
              <div><Label className="text-xs">Máx. atendentes</Label><Input type="number" min="0" value={f.max_users} onChange={set("max_users")} placeholder="Ilimitado" data-testid="tenant-max-users-input" /></div>
              <div><Label className="text-xs">Máx. WhatsApp</Label><Input type="number" min="0" value={f.max_connections} onChange={set("max_connections")} placeholder="Ilimitado" data-testid="tenant-max-connections-input" /></div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Deixe em branco ou 0 para ilimitado.</p>
          </div>
          {!editing && (
            <div className="border-t border-border pt-3 space-y-3">
              <div className="text-sm font-semibold">Administrador da empresa</div>
              <div><Label>Nome</Label><Input value={f.admin_name} onChange={set("admin_name")} data-testid="tenant-admin-name-input" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>E-mail</Label><Input type="email" value={f.admin_email} onChange={set("admin_email")} data-testid="tenant-admin-email-input" /></div>
                <div><Label>Senha inicial</Label><Input type="text" value={f.admin_password} onChange={set("admin_password")} data-testid="tenant-admin-password-input" /></div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tenant-save-button">{editing ? "Salvar" : "Criar empresa"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ icon: Icon, v, label }) => (
  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title={label}><Icon className="w-3.5 h-3.5" /> {v}</span>
);

export default function PlatformPage() {
  const { refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/platform/tenants").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const toggle = async (t, active) => {
    try { await api.patch(`/platform/tenants/${t.id}`, { active }); toast.success(active ? "Empresa reativada" : "Empresa suspensa"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };
  const enter = async (t) => {
    if (t.is_home) await api.post("/platform/exit");
    else await api.post(`/platform/tenants/${t.id}/enter`);
    await refresh();
    window.location.href = "/inbox";
  };

  return (
    <div className="h-full overflow-auto p-6 lg:p-10">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="heading text-3xl font-extrabold flex items-center gap-2"><Building className="w-7 h-7 text-emerald-600" /> Empresas</h1>
            <p className="text-sm text-muted-foreground mt-1">Empresas clientes da plataforma. Cada uma vê somente os próprios dados.</p>
          </div>
          <Button onClick={() => setEditing({})} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tenant-new-button"><Plus className="w-4 h-4 mr-1" /> Nova empresa</Button>
        </div>
        <Card><CardContent className="p-0">
          {items.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center gap-4 p-4 border-b border-border" data-testid="tenant-row">
              <TenantLogo tenant={t} />
              <div className="flex-1 min-w-[200px]">
                <div className="font-semibold flex items-center gap-2 flex-wrap">
                  {t.name}
                  {t.is_home && <Badge variant="secondary" className="text-[10px]">Sua empresa</Badge>}
                  {!t.active && <Badge className="bg-rose-600 text-[10px]" data-testid="tenant-suspended-badge">Suspensa</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">{t.segment || "Sem segmento"} · Plano: <b data-testid="tenant-plan-label">{t.plan_name || "—"}</b> · Admin: {t.admin?.email || "—"}</div>
                <div className="flex gap-3 mt-1">
                  <Stat icon={Users} v={`${t.users}/${t.max_users || "∞"}`} label="Atendentes / limite" />
                  <Stat icon={Wifi} v={`${t.connected}/${t.connections}/${t.max_connections || "∞"}`} label="WhatsApp conectados / cadastrados / limite" />
                  <Stat icon={MessageSquare} v={t.chats} label="Conversas" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!t.is_home && <Switch checked={t.active} onCheckedChange={(v) => toggle(t, v)} data-testid={`tenant-active-${t.id}`} title="Ativa" />}
                <LogoButton t={t} onDone={load} />
                <Button size="sm" variant="outline" onClick={() => setEditing(t)} data-testid={`tenant-edit-${t.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" onClick={() => enter(t)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`tenant-enter-${t.id}`}><LogIn className="w-4 h-4 mr-1" /> Acessar</Button>
              </div>
            </div>
          ))}
        </CardContent></Card>
      </div>
      <TenantDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} initial={editing} onSaved={load} />
    </div>
  );
}
